import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus, Save, Trash2, X, Pencil } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function SitesAdmin({ onChanged }) {
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState({});
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const { data } = await api.get("/reference/all");
    const sorted = [...(data.sites || [])].sort((a, b) => a.name.localeCompare(b.name));
    setItems(sorted);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Name is required"); return; }
    setBusy("create");
    try {
      await api.post("/sites", { name });
      toast.success(`Added "${name}"`);
      setNewName("");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const save = async (id) => {
    const name = (editing[id] || "").trim();
    if (!name) { toast.error("Name is required"); return; }
    setBusy(id);
    try {
      await api.patch(`/sites/${id}`, { name });
      toast.success("Renamed");
      setEditing((e) => { const n = { ...e }; delete n[id]; return n; });
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove site "${name}" from the dropdown? Existing historical requests keep their site.`)) return;
    setBusy(id);
    try {
      await api.delete(`/sites/${id}`);
      toast.success("Deleted");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-5" data-testid="sites-admin">
      <div>
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#C0C0C0]" strokeWidth={2}/> Sites
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          The list of physical sites shown when allocating a contractor.
        </p>
      </div>

      <div className="flex gap-2 items-end max-w-md">
        <div className="flex-1 space-y-1.5">
          <label className="pnc-eyebrow">Add a new site</label>
          <Input
            data-testid="site-new-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Bruntingthorpe"
            onKeyDown={(e) => { if (e.key === "Enter") create(); }}
            className="bg-[#1E1E1E] border-[#2A2A2A]"
          />
        </div>
        <Button data-testid="site-create-btn" disabled={busy === "create"} onClick={create} className="gap-1.5 h-11 rounded-xl bg-[#C0C0C0] hover:bg-[#8A8A8A] text-[#0A0A0A]">
          <Plus className="w-4 h-4" strokeWidth={2}/> Add
        </Button>
      </div>

      <div className="border border-[#2A2A2A] rounded-lg overflow-hidden bg-[#1E1E1E]">
        <Table data-testid="sites-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-8">No sites yet.</TableCell></TableRow>
            )}
            {items.map((s) => {
              const isEditing = s.id in editing;
              return (
                <TableRow key={s.id} data-testid={`site-row-${s.id}`}>
                  <TableCell className="font-medium">
                    {isEditing ? (
                      <Input
                        data-testid={`site-edit-${s.id}`}
                        value={editing[s.id]}
                        onChange={(e) => setEditing({ ...editing, [s.id]: e.target.value })}
                        onKeyDown={(e) => { if (e.key === "Enter") save(s.id); if (e.key === "Escape") setEditing((x) => { const n = { ...x }; delete n[s.id]; return n; }); }}
                        autoFocus
                        className="max-w-[300px] bg-[#141414] border-[#2A2A2A]"
                      />
                    ) : (
                      <span>{s.name}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    {isEditing ? (
                      <>
                        <Button size="sm" data-testid={`site-save-${s.id}`} onClick={() => save(s.id)} disabled={busy === s.id} className="gap-1.5 bg-[#C0C0C0] hover:bg-[#8A8A8A] text-[#0A0A0A]">
                          <Save className="w-3.5 h-3.5"/> Save
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`site-cancel-${s.id}`} onClick={() => setEditing((x) => { const n = { ...x }; delete n[s.id]; return n; })}>
                          <X className="w-3.5 h-3.5"/>
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" data-testid={`site-edit-btn-${s.id}`} onClick={() => setEditing({ ...editing, [s.id]: s.name })} className="gap-1.5 border-[#2A2A2A] bg-[#141414] hover:bg-[#0A0A0A]">
                          <Pencil className="w-3.5 h-3.5"/> Rename
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`site-del-${s.id}`} aria-label={`Delete ${s.name}`} onClick={() => remove(s.id, s.name)} disabled={busy === s.id} className="text-[#F87171] hover:bg-[#1F0A0A]">
                          <Trash2 className="w-3.5 h-3.5"/>
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
